<template>
  <v-card class="base-card shadow">
    <v-card-title class="headline accent--text pb-4">
      {{ title }}
    </v-card-title>

    <v-card-text>
      <CardRow v-if="isEffContractVisibility">
        <v-checkbox
          class="rounded-lg mini-text field-edit d-inline-block"
          v-model="monography.commonWork.isEffContract"
          label="Работа входит в эффективный контракт"
          hide-details
          dense
          :disabled="isShowMode && !canChangeIsEffContractInShowMode"
          @change="updateMonography"
        />
      </CardRow>

      <CardAutocompleteRow
        v-model="monography.commonWork.year"
        :items="yearList"
        item-value="id"
        item-text="year"
        label="Год публикации"
        placeholder="Выберите год"
        :no-filter="false"
        :disabled="isShowMode"
        :required="!isShowMode"
        :error-messages="errorMessages.year"
        @input="
          $v.monography.commonWork.year.$touch()
          updateMonography()
        "
      />

      <CardFieldRow
        v-model="monography.commonWork.workName"
        label="Наименование монографии"
        placeholder="Добавьте наименование монографии"
        :disabled="isShowMode"
        :required="!isShowMode"
        :error-messages="errorMessages.workName"
        @input="
          $v.monography.commonWork.workName.$touch()
          updateMonography()
        "
      />

      <div
        v-if="!isShowMode"
        class="d-flex"
      >
        <UIAlert title="Необходимо добавить ссылку и/или прикрепить подтверждающий документ" />
      </div>

      <CardFieldRow
        v-model="monography.link"
        label="Ссылка"
        placeholder="Добавьте ссылку"
        hide-border-bottom
        :disabled="isShowMode"
        :error-messages="errorMessages.link"
        :required="!isShowMode"
        @input="
          $v.monography.link.$touch()
          updateMonography()
        "
      />

      <CardRow
        label="Подтверждающие материалы"
        class="mt-0"
      >
        <UIUploadFile
          acceptFileFormats=".pdf, .jpeg, .jpg, .doc, .docx"
          button-text="Добавить подтверждающие материалы"
          :maxFileWeightInMb="5"
          :is-show-mode="isShowMode"
          :multiple="true"
          :maxFiles="10"
          :fileList="linkFileList"
          @uploadFiles="uploadFiles"
          @removeFile="removeFile"
        />
      </CardRow>

      <CardFieldRow
        v-model="monography.doi"
        label="DOI для электронных изданий"
        placeholder="Добавьте DOI для электронных изданий"
        :disabled="isShowMode"
        :error-messages="errorMessages.doi"
        @input="
          $v.monography.doi.$touch()
          updateMonography()
        "
      />

      <CardFieldRow
        v-model="monography.tomeCount"
        type="number"
        label="Количество томов"
        placeholder="Добавьте количество томов"
        :disabled="isShowMode"
        :error-messages="errorMessages.tomeCount"
        :required="!isShowMode"
        @input="
          $v.monography.tomeCount.$touch()
          updateMonography()
        "
      />

      <CardFieldRow
        v-model="monography.pageCount"
        type="number"
        label="Количество страниц"
        placeholder="Добавьте количество страниц"
        :disabled="isShowMode"
        :required="!isShowMode"
        :error-messages="errorMessages.pageCount"
        @input="
          $v.monography.pageCount.$touch()
          updateMonography()
        "
      />

      <CardFieldRow
        v-model="monography.tome"
        type="number"
        label="Номер тома"
        placeholder="Добавьте номер тома"
        :disabled="isShowMode"
        :required="!isShowMode"
        :error-messages="errorMessages.tome"
        @input="
          $v.monography.tome.$touch()
          updateMonography()
        "
      />
    </v-card-text>
  </v-card>
</template>

<script>
import { Monography } from '@/models'
import { required, maxLength, requiredIf } from 'vuelidate/lib/validators'
import { regexFrom1To99, regexFrom1To9999 } from '@/scripts/validation'
import { setValidateMessages } from '../../../scripts/validation'
import CardFieldRow from '../../ui/row/CardFieldRow'
import CardRow from '@/components/ui/row/CardRow'
import CardAutocompleteRow from '../../ui/row/CardAutocompleteRow'
import { mapGetters } from 'vuex'
import UIUploadFile from '@/components/ui/UIUploadFile.vue'
import UIAlert from '@/components/ui/UIAlert.vue'
import { isEffContractMixin } from '@/mixins/isEffContract'

export default {
  components: { UIAlert, UIUploadFile, CardAutocompleteRow, CardRow, CardFieldRow },
  props: {
    isShowMode: Boolean,
    selectedItem: Object,
    title: String,
    linkFileList: Array
  },

  mixins: [isEffContractMixin],

  mounted() {
    this.updateInvalid()
  },

  data: () => ({
    monography: new Monography()
  }),

  validations() {
    return {
      monography: {
        commonWork: {
          workName: { required, maxLength: maxLength(500) },
          year: { required }
        },
        link: { required: requiredIf(() => !this.linkFileList.length), maxLength: maxLength(500) },
        doi: { maxLength: maxLength(100) },
        tomeCount: { required, regexFrom1To99 },
        pageCount: { required, regexFrom1To9999 },
        tome: { required, regexFrom1To99 },
        chapterNumber: { regexFrom1To99 }
      }
    }
  },

  computed: {
    ...mapGetters('dictionaries', ['yearList']),

    currentWork() {
      return this.monography
    },

    errorMessages() {
      const allErrors = {}

      Object.keys(this.$v.monography.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.monography[key])
      })

      Object.keys(this.$v.monography.commonWork.$params).forEach(key => {
        allErrors[key] = setValidateMessages(this.$v.monography.commonWork[key])
      })

      return allErrors
    }
  },

  methods: {
    updateInvalid() {
      this.$emit('updateInvalid', this.$v.$invalid)
    },

    async updateMonography() {
      this.$emit('update:selected-item', new Monography(this.monography.getForAPI()))
      this.updateInvalid()
    },

    uploadFiles(files) {
      this.$emit('uploadLinkFiles', files)
      this.updateMonography()
    },

    removeFile(index) {
      this.$emit('removeLinkFile', index)
      this.updateMonography()
    }
  },

  watch: {
    selectedItem: {
      immediate: true,
      deep: true,
      handler(val) {
        this.monography = new Monography(val.getForAPI())
        this.updateInvalid()
      }
    }
  }
}
</script>

<style scoped></style>
